import { useEffect, useState } from 'react';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, AlertCircle, Loader2, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function AdminLLMConfigPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API}/api/admin/llm-config`, getAuth());
      setConfig(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load LLM config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSave = async () => {
    if (!keyInput.trim()) {
      toast.error('Enter a key first (or use Clear to remove the existing one)');
      return;
    }
    setSaving(true);
    try {
      await api.put(`${API}/api/admin/llm-config`, { gemini_api_key: keyInput.trim() }, getAuth());
      toast.success('Gemini API key saved');
      setKeyInput('');
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear the saved Gemini API key? AI features will stop working until a new key is set.')) return;
    setSaving(true);
    try {
      await api.put(`${API}/api/admin/llm-config`, { gemini_api_key: '' }, getAuth());
      toast.success('Gemini API key cleared');
      setKeyInput('');
      await fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Clear failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CollapsibleLayout>
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">LLM Configuration</h1>
          <p className="text-sm text-muted-foreground mt-1">
            API key powering PD-AI, the design decoder, SGI portal note enhancement, and AI task-instruction parsing.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : (
          <>
            <div className="rounded-lg border p-4 space-y-3 bg-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Provider: Google Gemini</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Text model: <code>{config?.model_text}</code> · Vision model: <code>{config?.model_vision}</code>
                  </div>
                </div>
                {config?.gemini_configured ? (
                  <span className="flex items-center gap-1 text-sm text-emerald-600">
                    <CheckCircle2 size={16} /> Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-sm text-amber-600">
                    <AlertCircle size={16} /> Not configured
                  </span>
                )}
              </div>

              {config?.gemini_configured && (
                <div className="text-sm text-muted-foreground">
                  Current key: <code>{config.gemini_api_key_preview}</code>
                  {config.updated_at && (
                    <span className="ml-2 text-xs">
                      · last updated {new Date(config.updated_at).toLocaleString()} by {config.updated_by || 'unknown'}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-4 space-y-4 bg-card">
              <div>
                <Label htmlFor="gemini-key">
                  {config?.gemini_configured ? 'Replace Gemini API Key' : 'Set Gemini API Key'}
                </Label>
                <div className="flex gap-2 mt-2">
                  <div className="flex-1 relative">
                    <Input
                      id="gemini-key"
                      type={showKey ? 'text' : 'password'}
                      placeholder="AIza…"
                      value={keyInput}
                      onChange={(e) => setKeyInput(e.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey(!showKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showKey ? 'Hide key' : 'Show key'}
                    >
                      {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <Button onClick={handleSave} disabled={saving || !keyInput.trim()}>
                    {saving ? <Loader2 className="animate-spin" size={16} /> : 'Save'}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Get a free key at{' '}
                  <a
                    href="https://aistudio.google.com/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    aistudio.google.com/apikey <ExternalLink size={12} />
                  </a>
                  . Free tier: 15 req/min, 1500 req/day. No credit card required.
                </p>
              </div>

              {config?.gemini_configured && (
                <div className="pt-2 border-t">
                  <Button variant="ghost" onClick={handleClear} disabled={saving} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    Clear saved key
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Disables all AI features until a new key is set.
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-medium mb-1">Heads up about free-tier Gemini</div>
              <ul className="list-disc list-inside space-y-1 text-amber-800">
                <li>Prompts may be logged by Google for product improvement. Don't send client PII without checking your client agreement.</li>
                <li>Throttling is real: 15 requests/minute is fine for personal use but can choke if multiple users hit decode/PD-AI simultaneously.</li>
                <li>Quality is meaningfully weaker than GPT-4o on the decoder. Good enough for an internal tool; not production-grade.</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </CollapsibleLayout>
  );
}
